import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import Badge from '../common/Badge';
import Button from '../common/Button';
import toast from 'react-hot-toast';
import { getCoverUrlWithFallback, fetchCoverMapping } from '../../utils/coverResolver';

const BookCard = ({ book, isAdmin = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [coverMapping, setCoverMapping] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchCoverMapping()
      .then((m) => {
        if (cancelled) return;
        setCoverMapping(m || {});
      })
      .catch(() => {
        if (cancelled) return;
        setCoverMapping({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const normalizedBook = {
    id: book.id,
    title: book.title || book.titre,
    author: book.author || book.auteur_nom,
    category: book.category || book.categorie_nom,
    genre: book.genre,
    publication_year: book.publication_year || book.annee_publication,
    copies_available: book.copies_available,
    location: book.location,
    status: book.status || book.statut || 'disponible',
    cover_url: book.cover_url || book.couverture_url || book.image_url || book.image_couverture || book.image,
  };
  
  useEffect(() => {
    const src = getCoverUrlWithFallback(
      { title: normalizedBook.title, author: normalizedBook.author },
      coverMapping || {},
      normalizedBook.cover_url,
    );
    setImgSrc(src);
  }, [normalizedBook.title, normalizedBook.author, normalizedBook.cover_url, coverMapping]);

  const getStatusVariant = (statut) => {
    switch (statut) {
      case 'disponible': return 'success';
      case 'reserve_complet': return 'danger';
      case 'maintenance': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (statut) => t(`book_status.${statut}`, statut);

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/admin/books/${book.id}/edit`);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toast.error('Action non disponible sur la version statique');
  };

  return (
    <Link to={isAdmin ? '#' : `/books/${book.id}`} className="group h-full block outline-none">
      <motion.div 
        whileHover={{ y: -8, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
        className="h-full flex flex-col bg-white dark:bg-dark-surface rounded-2xl shadow-sm hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-300 border border-gray-100 dark:border-gray-800 overflow-hidden"
      >

        {/* Cover Image Container */}
        <div className="relative aspect-[2/3] overflow-hidden bg-gray-100 dark:bg-gray-800">
          {imgSrc ? (
            <motion.img
              layoutId={`book-cover-${book.id}`}
              src={imgSrc}
              alt={normalizedBook.title}
              width="200"
              height="300"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy"
              onError={() => {
                const fallback = getCoverUrlWithFallback(
                  { title: normalizedBook.title, author: normalizedBook.author },
                  {},
                  null,
                );
                setImgSrc(fallback);
              }}
            />
          ) : (
            <motion.div layoutId={`book-cover-${book.id}`} className="w-full h-full flex flex-col items-center justify-center text-gray-400">
              <BookOpen className="h-12 w-12 mb-2 opacity-50" />
              <span className="text-xs uppercase tracking-wider font-medium">No Cover</span>
            </motion.div>
          )}

          {/* Overlay Gradient on Hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

          {/* Status Badge */}
          <div className="absolute top-3 right-3 shadow-sm z-10">
            <Badge variant={getStatusVariant(normalizedBook.status)} size="xs" className="uppercase tracking-wide font-bold backdrop-blur-md bg-white/90 dark:bg-black/50 border border-white/20">
              {getStatusLabel(normalizedBook.status)}
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col flex-grow p-4 md:p-5 relative">
          {/* Category */}
          <div className="mb-2">
            <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest bg-primary-50 dark:bg-primary-900/20 px-2 py-1 rounded-full">
              {normalizedBook.category || normalizedBook.genre || t('common.uncategorized')}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight mb-1.5 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
            {normalizedBook.title}
          </h3>

          {/* Author */}
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-1">
            {t('common.by')} <span className="font-medium text-gray-800 dark:text-gray-200">{normalizedBook.author}</span>
          </p>

          <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-800/50 flex items-center justify-between text-xs text-gray-500 dark:text-gray-500">
            {/* Year */}
            <span className="font-mono bg-gray-50 dark:bg-gray-800/50 px-2.5 py-1 rounded-md border border-gray-100 dark:border-gray-700">
              {normalizedBook.publication_year || 'N/A'}
            </span>

            {/* Simple visual indicator for location/copies */}
            <div className="flex items-center gap-2">
              {normalizedBook.location && (
                <span className="flex items-center" title={t('book.location')}>
                  <MapPin className="w-3.5 h-3.5 mr-1 text-primary-400" />
                  {normalizedBook.location.length > 5 ? 'Biblio' : normalizedBook.location}
                </span>
              )}
            </div>
          </div>

          {/* Admin Actions */}
          {isAdmin && (
            <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <Button size="xs" variant="outline" onClick={handleEdit} className="flex-1 text-xs">
                {t('common.edit')}
              </Button>
              <Button size="xs" variant="danger" onClick={handleDelete} className="flex-1 text-xs">
                {t('common.delete')}
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

export default BookCard;
